/**
 * PricingStep — quote the package against service-pricing.
 *
 * The step takes the captured sender/receiver/package + service class, calls
 * `getQuote()` and renders either a breakdown or a typed `QuoteFailure`. The
 * operator clicks "Lock quote and continue" to advance — that writes the
 * pricing snapshot back to the draft (amountNgn + quoteId via tariffId).
 *
 * Zone codes are resolved from the state name via STATE_TO_ZONE, which mirrors
 * the mapping in service-pricing/src/modules/quote/quote.controller.ts. This
 * ensures the frontend sends zone codes that match the seeded tariffs in
 * prisma/pricing/db-migrations/seed-tariffs.sql (LAGOS_METRO, SOUTH_WEST,
 * SOUTH_EAST, SOUTH_SOUTH, NORTH_CENTRAL, NORTH_WEST, NORTH_EAST).
 *
 * Single source of truth for the mapping lives in the backend (quote.controller.ts).
 * The frontend mirrors it for the operator UI path (POST /v1/pricing/quote);
 * citizens use POST /v1/pricing/quote-mine which resolves zone codes server-side.
 */
import { type JSX, useEffect, useState } from 'react';

import { getQuote } from '../../lib/api/pricing';
import type {
  PackagePayload,
  PartyPayload,
  PricingPayload,
  QuoteResult,
  ServiceClass,
} from '../../lib/api/types';

import { formatMoney } from './formatMoney';

interface PricingStepProps {
  sender?: PartyPayload;
  receiver?: PartyPayload;
  pkg?: PackagePayload;
  serviceClass?: ServiceClass;
  onLockQuote: (pricing: PricingPayload) => void;
  onBack: () => void;
}

/**
 * Maps Nigerian state names (case-insensitive) to pricing zone codes seeded
 * in prisma/pricing/db-migrations/seed-tariffs.sql.
 *
 * Mirrors STATE_TO_ZONE in apps/service-pricing/src/modules/quote/quote.controller.ts.
 * Update both if zone codes or state aliases change.
 */
const STATE_TO_ZONE: Record<string, string> = {
  // Lagos Metro
  lagos:         'LAGOS_METRO',
  'lagos state': 'LAGOS_METRO',
  // South West
  ogun:    'SOUTH_WEST',
  oyo:     'SOUTH_WEST',
  osun:    'SOUTH_WEST',
  ondo:    'SOUTH_WEST',
  ekiti:   'SOUTH_WEST',
  // South East
  abia:    'SOUTH_EAST',
  anambra: 'SOUTH_EAST',
  ebonyi:  'SOUTH_EAST',
  enugu:   'SOUTH_EAST',
  imo:     'SOUTH_EAST',
  // South South
  akwaibom:      'SOUTH_SOUTH',
  'akwa ibom':   'SOUTH_SOUTH',
  bayelsa:       'SOUTH_SOUTH',
  'cross river': 'SOUTH_SOUTH',
  crossriver:    'SOUTH_SOUTH',
  delta:         'SOUTH_SOUTH',
  edo:           'SOUTH_SOUTH',
  rivers:        'SOUTH_SOUTH',
  // North Central (+ FCT)
  benue:    'NORTH_CENTRAL',
  kogi:     'NORTH_CENTRAL',
  kwara:    'NORTH_CENTRAL',
  nasarawa: 'NORTH_CENTRAL',
  niger:    'NORTH_CENTRAL',
  plateau:  'NORTH_CENTRAL',
  abuja:    'NORTH_CENTRAL',
  fct:      'NORTH_CENTRAL',
  'federal capital territory': 'NORTH_CENTRAL',
  'fct abuja':                 'NORTH_CENTRAL',
  // North West
  jigawa:  'NORTH_WEST',
  kaduna:  'NORTH_WEST',
  kano:    'NORTH_WEST',
  katsina: 'NORTH_WEST',
  kebbi:   'NORTH_WEST',
  sokoto:  'NORTH_WEST',
  zamfara: 'NORTH_WEST',
  // North East
  adamawa: 'NORTH_EAST',
  bauchi:  'NORTH_EAST',
  borno:   'NORTH_EAST',
  gombe:   'NORTH_EAST',
  taraba:  'NORTH_EAST',
  yobe:    'NORTH_EAST',
};

/** Fallback zone when state is unrecognised. Matches the backend DEFAULT_ZONE. */
const DEFAULT_ZONE = 'NORTH_CENTRAL';

function deriveZoneCode(party?: PartyPayload): string {
  if (!party?.state) {
    return DEFAULT_ZONE;
  }
  return STATE_TO_ZONE[party.state.toLowerCase().trim()] ?? DEFAULT_ZONE;
}

function kgToGrams(kg?: string): number | null {
  if (!kg) {
    return null;
  }
  // Multiply by 1000 via string manipulation? simplest: parseFloat and round.
  // We're not doing money math; weight is fine to use Number.
  const n = Number(kg);
  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }
  return Math.round(n * 1000);
}

function mapServiceClass(sc?: ServiceClass): string | null {
  if (!sc) {
    return null;
  }
  // Backend ServiceClass values are pre-mapped uppercase codes.
  return sc;
}

export function PricingStep({
  sender,
  receiver,
  pkg,
  serviceClass,
  onLockQuote,
  onBack,
}: PricingStepProps): JSX.Element {
  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const weightGrams = kgToGrams(pkg?.weightKg);
  const serviceClassCode = mapServiceClass(serviceClass);
  const ready = sender && receiver && pkg && weightGrams !== null && serviceClassCode !== null;

  useEffect(() => {
    if (!ready) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const result = await getQuote({
          originZoneCode: deriveZoneCode(sender),
          destZoneCode: deriveZoneCode(receiver),
          serviceClassCode: serviceClassCode as string,
          weightGrams: weightGrams as number,
          declaredValueAmount: pkg?.declaredValueNgn,
          declaredValueCurrency: pkg?.declaredValueNgn ? 'NGN' : undefined,
        });
        if (!cancelled) {
          setQuote(result);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Quote failed');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    ready,
    sender,
    receiver,
    pkg,
    serviceClassCode,
    weightGrams,
  ]);

  const handleLock = (): void => {
    if (quote && quote.ok) {
      onLockQuote({ amountNgn: quote.amount, quoteId: quote.tariffId });
    }
  };

  return (
    <section className="space-y-4" data-testid="pricing-step">
      <header>
        <h2 className="text-xl font-semibold text-slate-900">Pricing</h2>
        <p className="text-sm text-slate-600">
          Live quote based on package details.
        </p>
      </header>

      {!ready && (
        <div className="rounded border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
          Complete sender, receiver and package details before quoting.
        </div>
      )}

      {ready && loading && (
        <div className="rounded border border-slate-200 bg-white p-4 text-sm text-slate-600">
          Calculating…
        </div>
      )}

      {ready && error !== null && (
        <div className="rounded border border-red-300 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {ready && quote && quote.ok && (
        <div
          className="rounded border border-emerald-300 bg-emerald-50 p-4"
          data-testid="quote-result"
        >
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-medium text-emerald-700">Quoted price</span>
            <span className="text-2xl font-semibold text-emerald-900">
              {formatMoney(quote.amount, quote.currency)}
            </span>
          </div>
          <ul className="mt-3 space-y-1 text-sm text-emerald-900">
            {quote.lines.map((line, i) => (
              <li key={i} className="flex justify-between">
                <span>{line.description}</span>
                <span className="font-mono">{formatMoney(line.amount, quote.currency)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-emerald-700">
            Tariff {quote.tariffId} — valid until {new Date(quote.validUntil).toLocaleString()}
          </p>
        </div>
      )}

      {ready && quote && !quote.ok && (
        <div
          className="rounded border border-red-300 bg-red-50 p-4 text-sm text-red-700"
          data-testid="quote-failure"
        >
          <strong>Cannot quote — {quote.reason}</strong>
          {quote.detail !== undefined && <p className="mt-1">{quote.detail}</p>}
        </div>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleLock}
          disabled={!quote || !quote.ok}
          data-testid="pricing-continue"
          className="rounded bg-nipost-600 px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-300"
        >
          Lock quote and continue
        </button>
      </div>
    </section>
  );
}
